import { GenericFormField } from '@/components/form/generic-form-field'
import { Input, InputProps } from '@/components/ui/input'
import { IClassNameProps } from '@/utils/typings/class-name-props'
import { ControllerRenderProps, FieldValues, Path, useFormContext } from 'react-hook-form'
import { JSX, KeyboardEvent, useCallback } from 'react'

interface InputGroupProps<T extends FieldValues> extends Omit<InputProps, 'name' | 'onSubmit'> {
    label?: string
    labelClassName?: IClassNameProps['className']
    name: Path<T>
    containerClassName?: IClassNameProps['className']
    onSubmit?: (e: KeyboardEvent<HTMLInputElement>) => void
    hideFormMessage?: boolean
    render?: ({
        field,
    }: {
        field: ControllerRenderProps<FieldValues, Path<T>>
        renderDefaultInput: (field: ControllerRenderProps<FieldValues, Path<T>>) => React.ReactNode | JSX.Element
    }) => React.ReactNode
}

export const InputGroup = <T extends FieldValues>({
    label,
    labelClassName,
    name,
    containerClassName,
    onChange,
    onFocus,
    onSubmit,
    onKeyDown,
    hideFormMessage,
    render,
    ...props
}: InputGroupProps<T>) => {
    const { clearErrors } = useFormContext()

    const renderFieldInput = useCallback(
        ({ field }: { field: ControllerRenderProps<FieldValues, Path<T>> }) => {
            return (
                <Input
                    {...field}
                    onChange={(e) => {
                        onChange?.(e)
                        clearErrors(name)
                    }}
                    onFocus={(e) => {
                        onFocus?.(e)
                        clearErrors(name)
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            onSubmit?.(e)
                        }

                        onKeyDown?.(e)
                    }}
                    sizeType="md"
                    // 16px font leads to ios not zooming into the input field.
                    className="text-[16px] md:text-[14px]"
                    {...props}
                />
            )
        },
        [props, onChange, clearErrors, name, onFocus, onKeyDown, onSubmit]
    )

    return (
        <GenericFormField
            name={name}
            label={label}
            labelClassName={labelClassName}
            itemClassName={containerClassName}
            hideFormMessage={hideFormMessage}
            render={({ field }) => {
                if (render) {
                    return render({
                        field,
                        renderDefaultInput: (field) => renderFieldInput({ field }),
                    })
                }

                return renderFieldInput({ field })
            }}
        />
    )
}
